import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { getTranslation } from 'common/react/utils/translations';

/**
 * React component that renders HTML content with a "Read More / Read Less" toggle.
 * Truncates long content while preserving valid HTML.
 *
 * @component
 *
 * @param {object} props
 * @param {string} props.htmlContent - The HTML string to render (will be injected via `dangerouslySetInnerHTML`).
 * @param {number} [props.maxChars] - Maximum number of characters to show before truncating.
 * @param {string} [props.className] - Additional class names for the container.
 *
 * @returns {JSX.Element} Rendered component.
 */
export default function TextWithReadMore({
    htmlContent,
    maxChars = 200,
    className = ''
}) {
    const [expanded, setExpanded] = useState(false);

    const textOnly = htmlContent.replace(/<[^>]*>?/gm, '');
    const needsTruncate = textOnly.length > maxChars;

    const displayHtml = expanded
        ? htmlContent
        : PULSE.app.common.content.truncateText(htmlContent, maxChars);

    return (
        <>
            <div
                className={`${className} text-with-read-more`}
                dangerouslySetInnerHTML={{ __html: displayHtml }}
            />
            {needsTruncate && (
                <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="text-with-read-more__button"
                >
                    {expanded
                        ? getTranslation('label.showLess')
                        : getTranslation('label.showMore')}
                </button>
            )}
        </>
    );
}

TextWithReadMore.propTypes = {
    htmlContent: PropTypes.string.isRequired,
    maxChars: PropTypes.number,
    className: PropTypes.string
};
